// Signature-stable delegate. The composition core — the offline union, its
// three helpers, the desktop error/loading suppression and the body of
// `useQueryCombinedSessions` — moved to `@heidi/session/data`. The module
// identity stays so every importer and every `jest.mock` literal on this path
// keeps resolving untouched: no call site changes.
//
// What is left here is the one thing the package cannot reach — the three list
// legs. `useQuerySessions` and `useQuerySessionsV2` are themselves delegates
// whose data cores are already in the package, but what remains of them is the
// review-context composition (whether this user may read another member's
// list), and the v3 paged search has not moved at all — it reads the
// session-list toolbar's filter/sort/search atoms. They cross as hook-valued
// parameters rather than through the config seam, because they are session
// reads that are merely uncarved, not app-shaped values; the parameters delete
// themselves when those two carve.
//
// The cohort/flag/desktop trio and the two offline-recording atoms do NOT
// cross here — they resolve in `src/hooks/useSessionListEnvironment.ts` and
// arrive through the required `configureSessionData` member wired at
// `src/utils/session-data-wiring.ts`.
import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import {
  onlyPendingServerDeletes,
  withoutPendingServerDeletes,
} from '@heidi/session/contracts';
import { useQueryCombinedSessions as useQueryCombinedSessionsCore } from '@heidi/session/data';
import { useMemo } from 'react';
import { useQuerySessions } from './useQuerySessions';
import { useQuerySessionsV2 } from './useQuerySessionsV2';
import { useQueryAdminSessions } from './useQuerySessionsWithPagination';

// The version-dispatched cache COMMANDS moved to `@heidi/session/data` with
// the D1 family (`sessionsListCache.ts`); re-exported so existing importers +
// `jest.mock` literals keep resolving until task Z1's codemod re-points them.
export {
  getFirstCachedSessionId,
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
  useCancelSessionsQuery,
} from '@heidi/session/data';

export function useQueryCombinedSessions({
  enabled,
  suppressOfflineSyntheticSessions = false,
}: {
  enabled?: boolean;
  suppressOfflineSyntheticSessions?: boolean;
} = {}) {
  const result = useQueryCombinedSessionsCore({
    enabled,
    suppressOfflineSyntheticSessions,
    useSessionsV1: useQuerySessions,
    useSessionsV2: useQuerySessionsV2,
    useSessionsV3: useQueryAdminSessions,
  });

  // Filtered HERE rather than in the core because the mirror is an app atom, and
  // an app atom never crosses into a package as an import — it would have to
  // become another required `useSessionListEnvironment` member. This delegate is
  // already the app-side boundary every consumer of the legacy list imports, so
  // one read covers the legacy side-nav list, the legacy search, the upcoming
  // tab, the appointments tab and the transcription picker. Applied to the
  // core's UNIONED result; see `withoutPendingServerDeletes` for why that
  // matters.
  const pendingServerDeleteIds = useOfflinePendingServerDeleteIds();
  const sessions = useMemo(
    () =>
      result.sessions &&
      withoutPendingServerDeletes(result.sessions, pendingServerDeleteIds),
    [result.sessions, pendingServerDeleteIds]
  );

  // The complement, for the one consumer class that must NOT read the filtered
  // array: the EHR appointment link check. Why, and what breaks without it, is
  // on `onlyPendingServerDeletes`.
  const sessionsHiddenByQueuedDelete = useMemo(
    () => onlyPendingServerDeletes(result.sessions ?? [], pendingServerDeleteIds),
    [result.sessions, pendingServerDeleteIds]
  );

  return {
    ...result,
    sessions,
    sessionsHiddenByQueuedDelete,
    // How many rows the list held BEFORE the queued deletes were removed.
    //
    // Pagination bookkeeping must not read `sessions.length`. The legacy
    // side-nav decides whether another page can exist by asking whether the
    // loaded list is at least one page long, and a read filter shrinking that
    // array below the page size reads as "the server had no more" — so one
    // queued delete on a full first page would permanently stop a clinician
    // with a long history from loading their older sessions.
    //
    // This is the length that check was always asking about: what the query
    // (plus the offline union) actually produced, independent of what is
    // displayed.
    loadedSessionCount: result.sessions?.length ?? 0,
  };
}
